import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import '../login.css';

interface LocationState {
  from?: string;
}

const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M3 9h11M10 4.5 14.5 9 10 13.5"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function Login() {
  const { user, sendMagicLink, verifyOtp } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const from = (location.state as LocationState | null)?.from ?? '/difot';
    return <Navigate to={from} replace />;
  }

  const params = new URLSearchParams(location.search);
  const linkError = params.get('error');

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await sendMagicLink(email.trim());
      // Always advance regardless of whether the email exists — prevents
      // account enumeration.
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send sign-in email');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp(email.trim(), code.trim());
      // On success the provider sets `user`, and the <Navigate> above takes over.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify code');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="signin">
      {/* LEFT : form */}
      <section className="signin-left">
        <header className="signin-topbar">
          <img className="signin-logo" src="/brand/logo-color.png" alt="Bing Lee" />
          <a
            className="signin-sitelink"
            href="https://www.binglee.com.au/"
            target="_blank"
            rel="noopener noreferrer"
          >
            binglee.com.au&nbsp;→
          </a>
        </header>

        <div className="signin-center">
          <div className="signin-tagline">
            <span className="a">Reporting,</span>
            <span className="b">delivered.</span>
          </div>

          {linkError === 'expired_link' && (
            <p className="signin-error" role="alert">
              That sign-in link has expired. Request a fresh code below.
            </p>
          )}
          {linkError === 'invalid_link' && (
            <p className="signin-error" role="alert">
              That sign-in link wasn’t valid. Please request a fresh code below.
            </p>
          )}

          {step === 'email' ? (
            <>
              <p className="signin-eyebrow">Reporting Portal · Sign in</p>
              <h1>Welcome back.</h1>
              <p className="signin-lead">
                Enter your work email and we’ll send you a one-time code — no password needed.
              </p>

              {error && (
                <p className="signin-error" role="alert">
                  {error}
                </p>
              )}

              <form onSubmit={onRequest} noValidate>
                <div className="signin-field">
                  <div className="signin-field-head">
                    <label htmlFor="email">Email address</label>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="you@binglee.com.au"
                    autoComplete="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <button className="submit" type="submit" disabled={submitting || !email.trim()}>
                  {submitting ? (
                    <>
                      <span>Sending…</span>
                      <span className="spinner" />
                    </>
                  ) : (
                    <>
                      <span>Send me a code</span>
                      <ArrowIcon />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <p className="signin-eyebrow">Reporting Portal · Verify</p>
              <h1>Check your email.</h1>
              <p className="signin-lead">
                We sent a code to <strong>{email}</strong>. Enter it below, or click the link in the
                email.
              </p>

              {error && (
                <p className="signin-error" role="alert">
                  {error}
                </p>
              )}

              <form onSubmit={onVerify} noValidate>
                <div className="signin-field">
                  <div className="signin-field-head">
                    <label htmlFor="code">Verification code</label>
                    <button
                      type="button"
                      className="signin-link"
                      onClick={() => {
                        setStep('email');
                        setCode('');
                        setError(null);
                      }}
                    >
                      Use a different email
                    </button>
                  </div>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    className="code-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6,8}"
                    maxLength={8}
                    placeholder="••••••"
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  />
                </div>

                <button className="submit" type="submit" disabled={submitting || code.length < 6}>
                  {submitting ? (
                    <>
                      <span>Verifying…</span>
                      <span className="spinner" />
                    </>
                  ) : (
                    <>
                      <span>Verify &amp; sign in</span>
                      <ArrowIcon />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <p className="signin-legal">
            Authorised staff &amp; partner access · Need help?{' '}
            <a href="https://www.binglee.com.au/" target="_blank" rel="noopener noreferrer">
              Contact support
            </a>
          </p>
        </div>
      </section>

      {/* RIGHT : hero */}
      <aside className="signin-right" aria-hidden="true">
        <img className="signin-hero-img" src="/brand/hero.png" alt="" />
        <div className="signin-hero-tint" />
        <div className="signin-motif" />
        <div className="signin-brandmark">
          <div className="lockup">
            <img src="/brand/logo-white.png" alt="Bing Lee" />
            <span className="ds">REPORTING PORTAL</span>
          </div>
          <div className="tag">Real-time delivery &amp; service reporting.</div>
        </div>
      </aside>
    </div>
  );
}
